/**
 * Pure helper for the FEAT-003 power chart's per-connector sliding window.
 *
 * BUG-005 fix (Session 8f.9): the window is now wall-clock based, not
 * sample-count based, so the X axis advances even when the tab is throttled
 * (background tab missing ticks no longer compresses the visible series).
 *
 * Pulled out of PowerChart.tsx so the buffer maintenance can be unit-tested
 * without spinning up React.
 */
import type { ConnectorId } from '@/state/types';

export interface Sample {
  readonly t: number;
  readonly value: number;
}

interface ConnectorRead {
  readonly id: ConnectorId;
  readonly effectivePowerW: number;
}

/**
 * Append one new sample per connector and trim samples older than
 * `now - windowMs`. Connectors absent from the live `connectors` map are
 * dropped (topology change → buffer follows). Pure — call sites stash the
 * result themselves.
 */
export function sampleConnectors(
  prev: ReadonlyMap<number, readonly Sample[]>,
  connectors: ReadonlyMap<ConnectorId, ConnectorRead>,
  windowMs: number,
  now: number,
): ReadonlyMap<number, readonly Sample[]> {
  const cutoff = now - windowMs;
  const next = new Map<number, readonly Sample[]>();
  for (const c of connectors.values()) {
    const key = Number(c.id);
    const existing = prev.get(key) ?? [];
    const appended: Sample[] = [...existing, { t: now, value: c.effectivePowerW }];
    next.set(key, trimByTime(appended, cutoff));
  }
  return next;
}

/**
 * Push a literal zero-power sample at `now` for every connector currently
 * tracked. Used on WS close / tx stop so the chart visibly decays to zero
 * instead of looking frozen at the last positive sample.
 */
export function pushZeroSamples(
  prev: ReadonlyMap<number, readonly Sample[]>,
  windowMs: number,
  now: number,
): ReadonlyMap<number, readonly Sample[]> {
  const cutoff = now - windowMs;
  const next = new Map<number, readonly Sample[]>();
  for (const [key, samples] of prev) {
    const appended: Sample[] = [...samples, { t: now, value: 0 }];
    next.set(key, trimByTime(appended, cutoff));
  }
  return next;
}

function trimByTime(samples: readonly Sample[], cutoff: number): readonly Sample[] {
  // Keep at least one sample older than the cutoff so the polyline visibly
  // starts at the left edge rather than vanishing for paused / sparse series.
  let firstKeep = samples.findIndex((s) => s.t >= cutoff);
  if (firstKeep === -1) {
    // Everything is older than the cutoff — keep just the most recent point
    // so the chart still renders the dot drifting off-window.
    return samples.length === 0 ? samples : [samples[samples.length - 1] as Sample];
  }
  if (firstKeep > 0) firstKeep -= 1; // anchor the polyline at the left edge
  return firstKeep === 0 ? samples : samples.slice(firstKeep);
}
